import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { toast } from 'sonner';
import { fetchPaymentLink, startCheckout } from '../lib/api';
import { usePageMeta } from '../hooks/use-page-meta';

const serif = { fontFamily: 'Cormorant Garamond, serif', fontWeight: 300 };

const Shell = ({ children, testId }) => (
  <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center px-6" style={{ paddingTop: '100px', paddingBottom: '60px' }} data-testid={testId}>
    <div className="w-full max-w-md text-center p-10 border border-white/10 bg-[#242424] fade-in-up">{children}</div>
  </div>
);

export const PayPage = () => {
  const { id } = useParams();
  const [link, setLink] = useState(null);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  usePageMeta({ title: 'Secure Payment', description: 'Secure payment for your photo session with Elina K. Photography.', path: `/pay/${id}` });

  useEffect(() => {
    fetchPaymentLink(id).then(setLink).catch(() => setError('This payment link is not valid.'));
  }, [id]);

  const pay = async () => {
    setStarting(true);
    try {
      const { checkout_url } = await startCheckout(id);
      window.location.href = checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start the payment. Please try again.');
      setStarting(false);
    }
  };

  if (error) return <Shell testId="pay-page-error"><p className="text-[#b8b8b8]">{error}</p></Shell>;
  if (!link) return <Shell testId="pay-page-loading"><p className="text-[#888] text-sm">Loading…</p></Shell>;

  return (
    <Shell testId="pay-page">
      <p className="text-[10px] tracking-[0.3em] uppercase text-[#c9a87c] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>Elina K. Photography</p>
      <h1 className="text-3xl mb-2" style={serif} data-testid="pay-package">{link.package_name}</h1>
      <p className="text-[#b8b8b8] mb-6" data-testid="pay-client">for {link.client_name}</p>
      <div className="text-5xl text-[#c9a87c] mb-8" style={serif} data-testid="pay-amount">{link.amount.toLocaleString()} AED</div>
      {link.note && <p className="text-[#888] text-sm mb-8">{link.note}</p>}

      {link.status === 'paid' ? (
        <p className="text-emerald-300 tracking-widest uppercase text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }} data-testid="pay-already-paid">
          This payment has been completed. Thank you!
        </p>
      ) : (
        <>
          <button onClick={pay} disabled={starting} className="btn-submit w-full" data-testid="pay-now-btn">
            {starting ? 'Redirecting…' : 'Pay securely'}
          </button>
          <p className="flex items-center justify-center gap-2 text-[#666] text-xs mt-5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <Lock className="w-3.5 h-3.5" /> Secured by Ziina · Visa, Mastercard, Apple Pay & Google Pay
          </p>
        </>
      )}
    </Shell>
  );
};
